import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  Tv,
  Book,
  Coffee,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FittedLivingRoomsPage = () => {
  const livingRoomSolutions = [
    {
      name: 'Media Wall Units',
      subtitle: 'Entertainment Central',
      description: 'Comprehensive media solutions with integrated TV mounting, sound system housing, and stylish storage for all your entertainment needs.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Integrated TV mounting',
        'Cable management systems',
        'Sound bar housing',
        'Gaming console storage',
        'Remote control drawers',
        'LED backlighting options'
      ],
      priceRange: 'From £2,200',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Alcove Storage',
      subtitle: 'Space Maximization',
      description: 'Transform unused alcove spaces into beautiful fitted storage. Perfect for books, display items, and hidden everyday storage.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: [
        'Bespoke alcove fitting',
        'Adjustable shelving',
        'Hidden storage sections',
        'Display lighting',
        'Wine storage options',
        'Book and media storage'
      ],
      priceRange: 'From £1,800',
      color: 'text-green-600'
    },
    {
      name: 'Full Wall Solutions',
      subtitle: 'Complete Transformation',
      description: 'Transform entire walls with floor-to-ceiling fitted furniture. Combines media, storage, and display in one stunning design.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/7244dd71420ba78ea5eafb4e9e5b8337104c7b606cd79b52559728247fe1eaa5.png',
      features: [
        'Floor to ceiling design',
        'Multiple storage zones',
        'Integrated lighting',
        'Display areas',
        'Hidden compartments',
        'Bespoke configurations'
      ],
      priceRange: 'From £4,500',
      color: 'text-purple-600'
    }
  ];

  const livingRoomBenefits = [
    {
      icon: Tv,
      title: 'Entertainment Hub',
      description: 'Create the perfect entertainment center with integrated technology'
    },
    {
      icon: Book,
      title: 'Display & Storage',
      description: 'Beautiful display areas combined with hidden practical storage'
    },
    {
      icon: Coffee,
      title: 'Lifestyle Enhancement',
      description: 'Elevate your living space with bespoke fitted solutions'
    },
    {
      icon: Star,
      title: 'Space Optimization',
      description: 'Maximize every inch of your living room with smart design'
    }
  ];

  const livingRoomStyles = [
    {
      style: 'Contemporary',
      description: 'Clean lines with handleless designs',
      finishes: ['Alpine White', 'Natural Grey', 'Dust Grey', 'White Oak Effect'],
      features: ['Minimalist aesthetic', 'Hidden handles', 'LED integration']
    },
    {
      style: 'Traditional',
      description: 'Classic Shaker and Farmhouse styles',
      finishes: ['Soft Cream', 'Cashmere', 'Honey Castello Oak', 'Paintable'],
      features: ['Panel detailing', 'Classic proportions', 'Traditional handles']
    },
    {
      style: 'Modern',
      description: 'Bold colors and statement designs',
      finishes: ['Reed Green', 'Tyrolean Blue', 'Smoked Kala Ash', 'Grey Oak Effect'],
      features: ['Statement colors', 'Mixed materials', 'Architectural details']
    }
  ];

  const storageZones = [
    {
      zone: 'Entertainment Zone',
      items: ['TV equipment', 'Gaming consoles', 'Sound systems', 'Remote controls', 'DVDs/Games', 'Streaming devices']
    },
    {
      zone: 'Display Zone',
      items: ['Books', 'Ornaments', 'Photo frames', 'Plants', 'Art pieces', 'Collections']
    },
    {
      zone: 'Practical Zone',
      items: ['Magazines', 'Throws', 'Cushions', 'Board games', 'Stationery', 'General items']
    },
    {
      zone: 'Beverage Zone',
      items: ['Wine storage', 'Glassware', 'Bar accessories', 'Coffee supplies', 'Tea storage', 'Serving items']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Tv className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Living Solutions</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Fitted Living Rooms
                <span className="block" style={{color: '#8CB89F'}}>Transform Your Space</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> creates stunning fitted living room solutions. From media walls to alcove storage, transform your living space with bespoke furniture.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Living Room Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Design Consultation
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Fitted Living Rooms"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Fitted Living Room Furniture?</h2>
            <p className="text-xl text-gray-600">
              Maximize space, style, and functionality in your living area
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {livingRoomBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Living Room Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Living Room Solutions</h2>
            <p className="text-xl text-gray-600">
              Bespoke fitted furniture for every living space
            </p>
          </div>
          
          <div className="space-y-20">
            {livingRoomSolutions.map((solution, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={solution.image} 
                    alt={solution.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={solution.color}>{solution.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{solution.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{solution.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {solution.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{solution.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Style Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Style Options</h2>
            <p className="text-xl text-gray-600">
              Choose the perfect style for your living room
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {livingRoomStyles.map((style, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{style.style}</CardTitle>
                  <p className="text-gray-600">{style.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Available Finishes:</h4>
                    <div className="space-y-1">
                      {style.finishes.map((finish, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full border" style={{backgroundColor: '#8CB89F'}}></div>
                          <span className="text-sm text-gray-600">{finish}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Key Features:</h4>
                    <div className="space-y-1">
                      {style.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Star className="h-3 w-3 text-yellow-500" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Storage Zones */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Storage Zones</h2>
            <p className="text-xl text-gray-600">
              Organized storage for everything in your living room
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {storageZones.map((zone, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{zone.zone}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {zone.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" style={{color: '#8CB89F'}} />
                        <span className="text-sm text-gray-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Transform Your Living Room</h2>
          <p className="text-xl mb-8 opacity-90">
            Create the perfect living space with bespoke fitted furniture designed by Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Living Room Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Living Room Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FittedLivingRoomsPage;